import React, { ReactNode, useState } from 'react'
import { Box, Drawer, IconButton } from '@mui/material'
import { useSidebar } from './SidebarProvider'
import { useTranslation } from '../../hooks/useIntractionTranslate'
import { Bars3Icon } from '@heroicons/react/24/outline'
import { MaterialSidebar } from '../../../interaxDesignSystemComponents'
import { AnySidebarSection, SidebarNavigationItem, SidebarSection } from '../../types/navigation'
import { FACILITATOR_COLORS } from '../../../constants/colors'
import { AppGeneralContent } from '../Layout/AppGeneralContent'
import { INTERAX_COLORS } from '../../../constants/interaxColors'
import { SIDEBAR_WIDTH } from '../../../constants/layout'

/**
 * Props for the MaterialSidebarLayout component
 */
interface MaterialSidebarLayoutProps {
  children: ReactNode
  /** Rendered above the profile bar inside the sidebar (e.g. org switcher). */
  profileTopSlot?: React.ReactNode
  navigationItems: SidebarNavigationItem[]
  /** Section IDs that should show the live activity badge (e.g. when user has live activities) */
  liveActivitySectionIds?: AnySidebarSection[]
  /** Section IDs that should show the "soon" blinking badge (activity starting within 5 minutes) */
  soonActivitySectionIds?: AnySidebarSection[]
  /** Section IDs whose page holds feedback not read yet */
  newSectionIds?: AnySidebarSection[]
  notificationCountBySection?: Partial<Record<SidebarSection, number>>
}

/**
 * Material Sidebar Layout component with mobile drawer support
 * - Desktop (md+): Permanent sidebar
 * - Mobile (xs, sm): Temporary drawer with burger menu
 */
export const MaterialSidebarLayout: React.FC<MaterialSidebarLayoutProps> = ({
  children,
  profileTopSlot,
  navigationItems,
  liveActivitySectionIds,
  soonActivitySectionIds,
  newSectionIds,
  notificationCountBySection
}) => {
  const { t } = useTranslation()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const { isCollapsed } = useSidebar()

  const handleDrawerToggle = () => {
    setIsDrawerOpen(!isDrawerOpen)
  }

  const handleDrawerClose = () => {
    setIsDrawerOpen(false)
  }

  /**
   * Single React subtree for all breakpoints so crossing `md` (devtools / device toolbar) does not
   * remount facilitator content (e.g. {@link FlowProvider} / reflection wizard state).
   *
   * Previously: two separate `return` branches swapped the entire tree on `isDesktop`, unmounting
   * `AppGeneralContent` and all descendants.
   */
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'column', md: 'row' },
        height: '100vh',
        minHeight: 0,
        overflow: 'hidden',
        backgroundColor: FACILITATOR_COLORS.facilitatorDashboardBackground
      }}
    >
      {/* Mobile / narrow: top bar (hidden md+) */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'flex', md: 'none' },
          flexShrink: 0,
          alignItems: 'center',
          padding: '12px 16px',
          backgroundColor: FACILITATOR_COLORS.facilitatorSidebarBackground,
          borderBottom: `1px solid ${INTERAX_COLORS.thunderGrey20}`,
          zIndex: 1200
        }}
      >
        <IconButton
          onClick={handleDrawerToggle}
          aria-label={t('common.openNavigationMenu')}
          sx={{
            color: INTERAX_COLORS.white,
            '&:hover': {
              backgroundColor: INTERAX_COLORS.white10
            }
          }}
        >
          <Bars3Icon style={{ width: '24px', height: '24px' }} />
        </IconButton>
      </Box>

      {/* Desktop: permanent sidebar — keep node mounted; hide below md */}
      <Box
        sx={{
          display: {
            xs: 'none',
            sm: 'none',
            md: isCollapsed ? 'none' : 'block'
          },
          flexShrink: 0
        }}
      >
        <MaterialSidebar
          navigationItems={navigationItems}
          profileTopSlot={profileTopSlot}
          liveActivitySectionIds={liveActivitySectionIds}
          soonActivitySectionIds={soonActivitySectionIds}
          newSectionIds={newSectionIds}
          notificationCountBySection={notificationCountBySection}
        />
      </Box>

      <Drawer
        variant="temporary"
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        ModalProps={{
          keepMounted: true,
          disableScrollLock: true
        }}
        sx={{
          display: { xs: 'block', sm: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: `${SIDEBAR_WIDTH}px`
          }
        }}
      >
        <MaterialSidebar
          navigationItems={navigationItems}
          profileTopSlot={profileTopSlot}
          liveActivitySectionIds={liveActivitySectionIds}
          soonActivitySectionIds={soonActivitySectionIds}
          newSectionIds={newSectionIds}
          notificationCountBySection={notificationCountBySection}
          onDrawerClose={handleDrawerClose}
        />
      </Drawer>

      {/* Main content — same wrapper order at all breakpoints (last flex child) */}
      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <AppGeneralContent>{children}</AppGeneralContent>
      </Box>
    </Box>
  )
}
